import { useEffect, useMemo, useState, type ReactNode } from "react";
import { sitesVan } from "./AdvertentieList";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  EyeIcon,
  PencilIcon,
  Button,
  FormCard,
  Modal,
  SectionHeader,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { AdvertentieSnelActies } from "../components/AdvertentieSnelActies";
import { formatDateTime } from "../i18n/format";
import { fetchAdvertentieFotoBlob } from "../api/advertentieFoto";
import "./AdvertentiePreview.css";
import { AdvertentieConceptWizard, type ConceptAntwoorden } from "./AdvertentieConceptWizard";
import { AdvertentieIkZoekBaas } from "./AdvertentieIkZoekBaas";
import "./AdvertentieIkZoekBaas.css";
import { fetchMediaBlob, uploadMedia } from "../api/media";
import { fetchAnimalPhotoBlob } from "../api/animalPhoto";
import { PlatformWachtrijBanner, geraaktSinds, type PlatformWachtrij } from "./AdvertentiePlatformWachtrij";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

interface Kenmerk {
  id: string;
  sleutel: string;
  waarde: string | null;
  status: string;
  publiceerbaar: boolean | null;
  volgorde: number | null;
}
interface Foto {
  id: string;
  afbeeldingId: number | null;
  animalFileId: number | null;
  volgorde: number | null;
  altTekst: string | null;
  platformMediaId: string | null;
}
interface LogEntry {
  id: string;
  tijd: string | null;
  actor: string;
  actie: string | null;
  detail: string | null;
}

/** Mirrors api `AdvertentieDetailResponse`. */
interface AdvertentieDetailData {
  id: string;
  version: number;
  dierId: number | null;
  status: string;
  kanaal: string;
  /** Op welke sites dit dier staat: DIERMATCH en IKZOEKBAAS, elk true of false. */
  sites?: Record<string, boolean>;
  /**
   * De dag waarop dit dier de opvang uit ging, of null zolang het er nog is.
   *
   * <p>Staat het dier dan nog ergens op, dan hoort dat niet. Zonder deze datum was dat op het
   * scherm nergens te zien — zie de banner hieronder.
   */
  vertrokkenOp?: string | null;
  verouderd: boolean | null;
  publicatieBlokkade: boolean | null;
  publicatieBlokkadeReden: string | null;
  /** Hoe vaak de advertentie op de site bekeken is; null = niet bekend, niet nul. */
  basisNaam: string | null;
  /** De naam zoals hij op de site komt: zonder hokcode en zonder aantekening. */
  siteNaam: string | null;
  basisSoort: string | null;
  basisRas: string | null;
  basisGeslacht: string | null;
  basisLeeftijd: string | null;
  basisGeboortedatum: string | null;
  basisLocatie: string | null;
  basisBeschikbaarheid: string | null;
  /** Wel/niet/niet ingesteld — zie de keuze onderaan de editor. */
  buitenbeentje: boolean | null;
  /** Of de stand met de hand is gezet; dan laat het nachtelijke onderhoud het lint met rust. */
  lintHandmatig: boolean | null;
  hoofdfotoId: number | null;
  altTekst: string | null;
  tekstOverDitDier: string | null;
  tekstPastBij: string | null;
  tekstBelangrijk: string | null;
  /** Vrije DierMatch-labels ("Vriendelijk", "Actief"); ikzoekbaas gebruikt de kenmerkenlijst. */
  tags: string[];
  succesverhaal: string | null;
  afzenderNaam: string | null;
  afzenderAdres: string | null;
  afzenderTelefoon: string | null;
  afzenderEmail: string | null;
  afzenderUrl: string | null;
  externeId: string | null;
  publicatieUrl: string | null;
  publicatiedatum: string | null;
  laatsteSync: string | null;
  platformStatus: string | null;
  publicatieKanalen: string | null;
  foutmelding: string | null;
  foutcode: string | null;
  hersteladvies: string | null;
  goedgekeurdDoor: string | null;
  goedgekeurdOp: string | null;
  aangemaaktOp: string | null;
  gewijzigdOp: string | null;
  blokken: unknown[];
  kenmerken: Kenmerk[];
  fotos: Foto[];
  log: LogEntry[];
  /** De jongste zending naar DierMatch; null = deze advertentie ging nooit naar het platform. */
  platformWachtrij: PlatformWachtrij | null;
}

// De status volgt sinds de ikzoekbaas-vragenlijst uit het gekozen antwoord: leeg = ONBEKEND, elk
// gegeven antwoord = BEVESTIGD (ook "Nee" — dat is een antwoord dat op de site hoort te staan). Een
// aparte statuskeuze zou twee waarheden naast elkaar zetten.

/** The editor's editable form state (the loose text fields + alt-text + afzender + kenmerken). */
interface FormState {
  tekstOverDitDier: string;
  tekstPastBij: string;
  tekstBelangrijk: string;
  /** Kommagescheiden in het formulier; de lijstvorm ontstaat bij opslaan. */
  tags: string;
  succesverhaal: string;
  altTekst: string;
  afzenderNaam: string;
  afzenderTelefoon: string;
  afzenderEmail: string;
  afzenderUrl: string;
  buitenbeentje: boolean | null;
  hoofdfotoId: number | null;
  kenmerken: { id: string; sleutel: string; waarde: string | null; status: string; publiceerbaar: boolean }[];
}

type Notice =
  | {
      kind:
        | "saved" | "conflict" | "forbidden" | "error" | "klaar" | "approved" | "published" | "lifecycle"
        | "opnieuwVerstuurd" | "platformInWachtrij"
        | "aiFailed" | "aiApplied" | "aiPrivacyClean" | "aiGeenVoorstellen";
    }
  | { kind: "platformAfgewezen"; fout: string | null }
  | { kind: "onvolledig"; velden: string[] }
  | { kind: "publishBlocked"; redenen: string[] }
  | { kind: "aiPrivacyWarn"; warnings: PrivacyWaarschuwing[] };

/**
 * De melding na een handeling die (ook) naar het platform ging. Alleen als die handeling de wachtrij raakte:
 * anders zou een oude afwijzing als uitkomst van bijvoorbeeld goedkeuren verschijnen. `null` = de gewone
 * melding van de handeling geldt.
 */
function platformNotice(wachtrij: PlatformWachtrij | null | undefined, gestart: number): Notice | null {
  if (!wachtrij || !geraaktSinds(wachtrij, gestart)) return null;
  if (wachtrij.toestand === "WACHTEND") return { kind: "platformInWachtrij" };
  if (wachtrij.toestand === "AFGEWEZEN") return { kind: "platformAfgewezen", fout: wachtrij.laatsteFout };
  return null;
}

/** De platformwachtrij in één regel voor de historie: de toestand en het moment dat daarbij hoort. */
function wachtrijRegel(
  wachtrij: PlatformWachtrij,
  t: (key: string, options?: { args?: unknown[] }) => string,
  locale: string,
): string {
  const toestand = t(`advertentieWachtrijToestand.${wachtrij.toestand}`);
  const moment = wachtrij.verstuurdOp ?? wachtrij.afgewezenOp ?? wachtrij.laatstePoging ?? wachtrij.aangemaaktOp;
  return moment ? `${toestand} — ${formatDateTime(moment, locale)}` : toestand;
}

/** Eén open punt uit de publish-guard: de code zegt wat eraan te doen is, de tekst wat er mis is. */
interface GuardPunt {
  code: string;
  tekst: string;
  detail: string | null;
}

/** The publish-guard readout — mirrors GET /api/herplaatsing/{id}/publish-guard. */
interface PublishGuard {
  publiceerbaar: boolean;
  redenen: string[];
  punten?: GuardPunt[];
}

/** One AI privacy warning — mirrors api PrivacyWaarschuwing. */
interface PrivacyWaarschuwing {
  type: string;
  fragment: string;
  ernst: string;
}

function statusTone(status: string): StatusTone {
  if (status === "GEPUBLICEERD") return "success";
  if (status === "GEPLAATST") return "info";
  if (status === "KLAAR_VOOR_CONTROLE" || status === "GEPAUZEERD") return "warning";
  return "neutral";
}

/** Kommagescheiden invoer naar de lijst die de server verwacht; leegtes vallen weg. */
function splitsTags(invoer: string): string[] {
  return invoer.split(",").map((t) => t.trim()).filter((t) => t !== "");
}

function seed(a: AdvertentieDetailData): FormState {
  return {
    tekstOverDitDier: a.tekstOverDitDier ?? "",
    tekstPastBij: a.tekstPastBij ?? "",
    tekstBelangrijk: a.tekstBelangrijk ?? "",
    tags: (a.tags ?? []).join(", "),
    succesverhaal: a.succesverhaal ?? "",
    altTekst: a.altTekst ?? "",
    afzenderNaam: a.afzenderNaam ?? "",
    afzenderTelefoon: a.afzenderTelefoon ?? "",
    afzenderEmail: a.afzenderEmail ?? "",
    afzenderUrl: a.afzenderUrl ?? "",
    buitenbeentje: a.buitenbeentje ?? null,
    hoofdfotoId: a.hoofdfotoId ?? null,
    kenmerken: a.kenmerken.map((k) => ({
      id: k.id,
      sleutel: k.sleutel,
      waarde: k.waarde,
      status: k.status,
      publiceerbaar: !!k.publiceerbaar,
    })),
  };
}

/** One selectable photo — mirrors api `AdvertentieFotoOptieResponse`. */
interface FotoOptie {
  afbeeldingId: number;
  naam: string | null;
}

/** One Media & Documenten media file in the extra-photo pool — mirrors api MediaPoolItem. */
interface MediaPoolItem {
  animalFileId: number;
  fileName: string | null;
  contentType: string | null;
  image: boolean;
  inAdvertentie: boolean;
}

/** A thumbnail for a Media & Documenten file, fetched as an authenticated blob (bearer can't ride img). */
/**
 * De profielfoto van het dier, voor als de advertentie zelf nog geen hoofdfoto heeft. Zo staat er in de
 * preview het beeld dat de bezoeker straks ziet in plaats van een leeg vlak — het dier heeft immers al een
 * foto, alleen is die nog niet als advertentiefoto gekozen.
 */
function DierThumb({ dierId, alt }: { dierId: number; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let levend = true;
    fetchAnimalPhotoBlob(String(dierId))
      .then((blob) => { if (levend) setUrl(URL.createObjectURL(blob)); })
      .catch(() => { /* geen foto: het lege vlak blijft staan */ });
    return () => { levend = false; };
  }, [dierId]);
  // De klasse hoort erbij: zonder die klasse krijgt de foto haar eigen formaat en duwt ze de tekst
  // ernaast weg (gezien op acc bij een advertentie zonder eigen foto).
  return url
    ? <img src={url} alt={alt} className="fawkes-adv-foto__img" />
    : <span className="izb__leeg" aria-hidden="true" />;
}

function MediaThumb({ dierId, fileId, alt }: { dierId: string; fileId: number; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let obj: string | null = null;
    fetchMediaBlob(dierId, fileId, "inline")
      .then((blob) => {
        if (revoked) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => {});
    return () => {
      revoked = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [dierId, fileId]);
  return url ? (
    <img src={url} alt={alt} className="fawkes-adv-foto__img" />
  ) : (
    <span className="fawkes-adv-foto__img fawkes-adv-foto__img--loading" aria-hidden="true" />
  );
}

/**
 * De foto's van de advertentie als diavoorstelling: elke vijf seconden de volgende, met een zachte
 * overgang. Zo zie je in de preview alle foto's die de bezoeker straks kan doorbladeren in plaats van
 * alleen de eerste. Bij één foto gebeurt er niets — dan is het gewoon die foto.
 */
function FotoDia({ bladen, nu }: {
  bladen: Array<{ sleutel: string; beeld: ReactNode }>;
  nu: number;
}) {
  // Alle beelden blijven staan — anders haalt elke wisseling de foto opnieuw op en zie je een leeg vlak.
  // Alleen het zichtbare blad staat in de flow en bepaalt dus de maat van de kaart; de rest ligt eronder.
  return (
    <span className="fawkes-adv-dia">
      {bladen.map((b, i) => (
        <span key={b.sleutel} className={i === nu ? "fawkes-adv-dia__blad is-nu" : "fawkes-adv-dia__blad"}>
          {b.beeld}
        </span>
      ))}
    </span>
  );
}

/** A thumbnail that fetches the dier-scoped photo bytes with the bearer (a plain <img src> can't). */
function AuthedThumb({ advertentieId, afbeeldingId, alt }: { advertentieId: string; afbeeldingId: number; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let obj: string | null = null;
    fetchAdvertentieFotoBlob(advertentieId, afbeeldingId)
      .then((blob) => {
        if (revoked) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => {});
    return () => {
      revoked = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [advertentieId, afbeeldingId]);
  return url ? (
    <img src={url} alt={alt} className="fawkes-adv-foto__img" />
  ) : (
    <span className="fawkes-adv-foto__img fawkes-adv-foto__img--loading" aria-hidden="true" />
  );
}

type PreviewDevice = "desktop" | "mobile" | "instagram";

/** Op welk platform deze vestiging publiceert; bepaalt welke preview je ziet. */
type Kanaal = "DIERMATCH" | "IKZOEKBAAS" | "BEIDE";

/** Eén vraag uit de ikzoekbaas-lijst: de sleutel, de mogelijke antwoorden en of er een zin bij mag. */
interface Vraag {
  sleutel: string;
  antwoorden: string[];
  toelichting: boolean;
}

/**
 * DierMatch.nl-branded live preview of the ad (acc's Advertentie preview): a mock of the public listing
 * page rendered from the ad's own data, in a desktop / mobile / Instagram frame. Read-only — it mirrors
 * whatever the editor currently holds, so the operator sees the result before publishing.
 */
/**
 * De preview op ikzoekbaas.nl: dezelfde advertentiegegevens, in de opmaak van de publieke pagina van de
 * Dierenbescherming. De kenmerken staan daar als "label : waarde"-tabel en niet als vinkjes, dus een
 * bevestigd kenmerk wordt "Ja" (met de vrije waarde erbij als die er is) en een afgewezen kenmerk "Nee".
 */
function AdvertentiePreviewIkZoekBaas({ advertentieId, data, device, kanaalTeksten, onKenmerken, onFotos }: {
  advertentieId: string;
  data: AdvertentieDetailData;
  device: PreviewDevice;
  kanaalTeksten: { contact: string | null };
  onKenmerken: () => void;
  onFotos: () => void;
}) {
  const t = useTranslate();
  const naam = data.siteNaam ?? data.basisNaam ?? "—";
  const [diaNu, setDiaNu] = useState(0);
  const foto = data.hoofdfotoId != null
    ? <AuthedThumb advertentieId={advertentieId} afbeeldingId={data.hoofdfotoId} alt={data.altTekst ?? naam} />
    : data.dierId != null
      ? <DierThumb dierId={data.dierId} alt={data.altTekst ?? naam} />
      : <span className="izb__leeg" aria-hidden="true" />;

  // Alle foto's van de advertentie, de hoofdfoto voorop. Een foto zit er ofwel als advertentiefoto in
  // (afbeeldingId), ofwel als bestand uit Media & documenten (animalFileId) — allebei komen ze langs.
  const bladen: Array<{ sleutel: string; beeld: ReactNode }> = [];
  if (data.hoofdfotoId != null) {
    bladen.push({
      sleutel: `a${data.hoofdfotoId}`,
      beeld: <AuthedThumb advertentieId={advertentieId} afbeeldingId={data.hoofdfotoId} alt={data.altTekst ?? naam} />,
    });
  }
  for (const f of data.fotos) {
    if (f.afbeeldingId != null && f.afbeeldingId !== data.hoofdfotoId) {
      bladen.push({
        sleutel: `a${f.afbeeldingId}`,
        beeld: <AuthedThumb advertentieId={advertentieId} afbeeldingId={f.afbeeldingId} alt={f.altTekst ?? naam} />,
      });
    } else if (f.afbeeldingId == null && f.animalFileId != null && data.dierId != null) {
      bladen.push({
        sleutel: `m${f.animalFileId}`,
        beeld: <MediaThumb dierId={String(data.dierId)} fileId={f.animalFileId} alt={f.altTekst ?? naam} />,
      });
    }
  }
  const aantalBladen = bladen.length;
  // Elke vijf seconden de volgende foto; bij één foto staat de tijd stil.
  useEffect(() => {
    if (aantalBladen < 2) return;
    const id = setInterval(() => setDiaNu((i) => (i + 1) % aantalBladen), 5000);
    return () => clearInterval(id);
  }, [aantalBladen]);
  const nu = aantalBladen > 0 ? diaNu % aantalBladen : 0;
  const heroFoto = aantalBladen > 1 ? <FotoDia bladen={bladen} nu={nu} /> : undefined;

  const teksten = (
    [
      [t("advertentieDetailView.tekst.overDitDier"), data.tekstOverDitDier],
      [t("advertentieDetailView.tekst.pastBij"), data.tekstPastBij],
      [t("advertentieDetailView.tekst.belangrijk"), data.tekstBelangrijk],
    ] as Array<[string, string | null]>
  ).filter((r): r is [string, string] => !!r[1] && r[1].trim() !== "");

  // De vaste kenmerken van het dier staan op ikzoekbaas bovenaan de tabel, vóór de beoordeelde kenmerken.
  const vast: Array<{ label: string; waarde: string }> = [
    [t("advertentieDetailView.field.soort"), data.basisSoort],
    [t("advertentieDetailView.field.ras"), data.basisRas],
    [t("advertentieDetailView.field.geslacht"), data.basisGeslacht],
    [t("advertentieDetailView.field.geboortedatum"), nlDatum(data.basisGeboortedatum)],
  ].filter((r): r is [string, string] => !!r[1]).map(([label, waarde]) => ({ label, waarde }));

  // Op ikzoekbaas staat het gekozen antwoord er letterlijk ("Vanaf 12 jaar", "Nee, jachtinstinct"), en
  // een vraag die niemand beantwoord heeft staat er als "Onbekend". Alle vragen staan in de tabel — dat
  // is wat de bezoeker straks ziet, en het laat meteen zien wat er nog ingevuld moet worden.
  // Een kenmerk met "Publiceerbaar" uit gaat niet naar de site, dus ook niet in de preview (Alma, Roze, 2026-10-09).
  const kenmerken = data.kenmerken.filter((k) => k.publiceerbaar).map((k) => ({
    label: t(`advertentieKenmerk.${k.sleutel}`),
    waarde: k.waarde && k.waarde.trim() !== "" ? k.waarde : t("advertentieIkzoekbaas.onbekend"),
  }));

  return (
    <AdvertentieIkZoekBaas
      device={device}
      data={{
        naam,
        soort: data.basisSoort,
        ras: data.basisRas,
        geslacht: data.basisGeslacht,
        leeftijd: data.basisLeeftijd,
        geboortedatum: nlDatum(data.basisGeboortedatum),
        locatie: data.basisLocatie,
        adres: data.afzenderAdres,
        beschikbaarheid: data.basisBeschikbaarheid,
        telefoon: data.afzenderTelefoon,
        email: data.afzenderEmail,
        contactTekst: kanaalTeksten.contact,
        teksten,
        kenmerken: [...vast, ...kenmerken],
        onKenmerken,
        onFotos,
        foto,
        heroFoto,
        aantalFotos: aantalBladen,
        huidigeFoto: nu + 1,
      }}
    />
  );
}

/** De tekstvelden die je vanaf de preview kunt openen. */
type TekstVeld = "tekstOverDitDier" | "tekstPastBij" | "tekstBelangrijk";

/** Wat er in de bewerkpopup staat: één tekstblok, de tags, of de afzendergegevens. */
type VeldPopup =
  | { soort: "tekst"; veld: TekstVeld }
  | { soort: "tags" }
  | { soort: "afzender" };

/**
 * Wat je vanaf een blok in de DierMatch-preview kunt openen.
 *
 * <p>De preview was een kijkvenster: je zag wat er straks op de site staat, maar je kwam er niet bij.
 * Bewerken zat in één knop bovenaan die het hele formulier opende, en dan moest je zelf uitzoeken welk
 * veld bij welk stuk van de pagina hoorde. Nu hangt er aan elk blok een knopje dat précies dat stuk
 * opent -- de tekst, de tags, de kenmerken, de foto's of de afzender.
 */
export interface PreviewBewerk {
  tekst: (veld: TekstVeld) => void;
  tags: () => void;
  kenmerken: () => void;
  fotos: () => void;
  afzender: () => void;
}

/** Het knopje bij een blok. De titel zegt wát je opent; "Bewerken" alleen zou vijf keer hetzelfde zijn. */
function BlokKnop({ titel, onClick }: { titel: string; onClick: () => void }) {
  const t = useTranslate();
  return (
    <button
      type="button"
      className="fawkes-adv-preview__bewerk"
      onClick={onClick}
      title={titel}
      aria-label={titel}
    >
      <PencilIcon width={13} height={13} aria-hidden="true" />
      <span>{t("advertentieDetailView.bewerk.knop")}</span>
    </button>
  );
}

export function AdvertentiePreview({ advertentieId, data, device, bewerk }: {
  advertentieId: string; data: AdvertentieDetailData; device: PreviewDevice; bewerk?: PreviewBewerk;
}) {
  const t = useTranslate();
  const naam = data.siteNaam ?? data.basisNaam ?? "—";
  // Met alleen de waarde stond er een rij "Ja / Ja / Nee / Nee" onder de naam; de vraag erbij maakt
  // er weer informatie van.
  // DierMatch werkt hoofdzakelijk met tags (maintainer 2026-08-24): de vrije labels zíjn het
  // gezicht van de kaart. Kenmerken blijven de terugval voor advertenties van vóór de tags,
  // zodat die niet ineens kaal staan.
  const badges = (data.tags ?? []).length > 0
    ? data.tags.slice(0, 6)
    : data.kenmerken
        .filter((k) => k.status === "BEVESTIGD" && k.waarde)
        .slice(0, 4)
        .map((k) => `${t(`advertentieKenmerk.${k.sleutel}`)}: ${antwoordVan(k.waarde)}`);
  const meta = [data.basisLeeftijd, data.basisGeslacht, data.basisRas, data.basisSoort]
    .filter((v): v is string => !!v);
  // Photo gallery (all ad photos, main first) and the three free-text blocks that carry content.
  const gallery = data.fotos.filter((f) => f.afbeeldingId != null).slice(0, 8);
  // Met de bewerkknoppen erbij staan ook de lége blokken er: een blok dat je niet ziet kun je niet
  // invullen, en juist de ontbrekende tekst is wat je komt halen.
  const textBlocks = (
    [
      [t("advertentieDetailView.tekst.overDitDier"), data.tekstOverDitDier, "tekstOverDitDier"],
      [t("advertentieDetailView.tekst.pastBij"), data.tekstPastBij, "tekstPastBij"],
      [t("advertentieDetailView.tekst.belangrijk"), data.tekstBelangrijk, "tekstBelangrijk"],
    ] as Array<[string, string | null, TekstVeld]>
  ).filter((entry) => !!bewerk || (!!entry[1] && entry[1].trim() !== ""));
  // DierMatch werkt hoofdzakelijk met tags; de kenmerkentabel is hier bijzaak en toont alleen wat
  // beantwoord is. Een tabel vol "Onbekend" zegt een bezoeker niets — erger, hij leest als
  // onverschilligheid van het asiel. Invullen gaat via "Vragen invullen"; ikzoekbaas (waar de
  // vragenlijst het format ís) houdt zijn eigen preview met alle rijen.
  const beantwoord = data.kenmerken.filter((k) => {
    if (!k.publiceerbaar) return false;
    const antwoord = antwoordVan(k.waarde);
    return antwoord !== "" && antwoord.toLowerCase() !== "onbekend";
  });
  return (
    <div className={`fawkes-adv-preview fawkes-adv-preview--${device}`}>
      <div className="fawkes-adv-preview__frame">
        <header className="fawkes-adv-preview__nav">
          <span className="fawkes-adv-preview__brand">DierMatch<span>.nl</span></span>
          <nav className="fawkes-adv-preview__navlinks" aria-hidden="true">
            <span>Zoek een dier</span><span>Honden</span><span>Katten</span><span>Over ons</span><span>Contact</span>
          </nav>
        </header>
        <div className="fawkes-adv-preview__body">
          <div className="fawkes-adv-preview__hero">
            <div className="fawkes-adv-preview__info">
              <h2 className="fawkes-adv-preview__name">{naam}</h2>
              {badges.length > 0 && (
                <div className="fawkes-adv-preview__badges">
                  {badges.map((b, i) => <span key={i} className="fawkes-adv-preview__badge">{b}</span>)}
                </div>
              )}
              {data.basisLocatie && (
                <span className="fawkes-adv-preview__loc">{data.basisLocatie}</span>
              )}
              {meta.length > 0 && (
                <div className="fawkes-adv-preview__meta">
                  {meta.map((m, i) => <span key={i} className="fawkes-adv-preview__metaitem">{m}</span>)}
                </div>
              )}
              <span className="fawkes-adv-preview__cta">
                {t("advertentieDetailView.preview.cta", { args: [naam] })}
              </span>
              {bewerk && (
                <div className="fawkes-adv-preview__blokknoppen">
                  <BlokKnop titel={t("advertentieDetailView.bewerk.tags")} onClick={bewerk.tags} />
                </div>
              )}
            </div>
            <div className="fawkes-adv-preview__gallery-col">
              <div className="fawkes-adv-preview__photo">
                {data.hoofdfotoId != null ? (
                  <AuthedThumb advertentieId={advertentieId} afbeeldingId={data.hoofdfotoId} alt={data.altTekst ?? naam} />
                ) : data.dierId != null ? (
                  <DierThumb dierId={data.dierId} alt={data.altTekst ?? naam} />
                ) : (
                  <span className="fawkes-adv-preview__photo-empty" aria-hidden="true" />
                )}
              </div>
              {gallery.length > 0 && (
                <div className="fawkes-adv-preview__gallery">
                  {gallery.map((f) => (
                    <div key={f.id} className="fawkes-adv-preview__gallery-thumb">
                      <AuthedThumb advertentieId={advertentieId} afbeeldingId={f.afbeeldingId as number} alt={f.altTekst ?? naam} />
                    </div>
                  ))}
                </div>
              )}
              {bewerk && (
                <div className="fawkes-adv-preview__blokknoppen">
                  <BlokKnop titel={t("advertentieDetailView.bewerk.fotos")} onClick={bewerk.fotos} />
                </div>
              )}
            </div>
          </div>

          {textBlocks.length > 0 && (
            <div className="fawkes-adv-preview__texts">
              {textBlocks.map(([label, text, veld]) => (
                <section key={veld} className="fawkes-adv-preview__textcard">
                  <div className="fawkes-adv-preview__blokkop">
                    <h3 className="fawkes-adv-preview__textcard-title">{label}</h3>
                    {bewerk && <BlokKnop titel={label} onClick={() => bewerk.tekst(veld)} />}
                  </div>
                  {text && text.trim() !== "" ? (
                    <p className="fawkes-adv-preview__textcard-body">{text}</p>
                  ) : (
                    <p className="fawkes-adv-preview__blokleeg">{t("advertentieDetailView.bewerk.leeg")}</p>
                  )}
                </section>
              ))}
            </div>
          )}

          {(beantwoord.length > 0 || bewerk) && (
            <section className="fawkes-adv-preview__kenmerken">
              <div className="fawkes-adv-preview__blokkop">
                <h3 className="fawkes-adv-preview__section-title">{t("advertentieDetailView.section.kenmerken")}</h3>
                {bewerk && (
                  <BlokKnop titel={t("advertentieDetailView.bewerk.kenmerken")} onClick={bewerk.kenmerken} />
                )}
              </div>
              {beantwoord.length > 0 ? (
                <ul className="fawkes-adv-preview__kenmerk-list">
                  {beantwoord.map((k) => {
                    const antwoord = antwoordVan(k.waarde);
                    const nee = antwoord.toLowerCase().startsWith("nee");
                    // "Onbekend" is een antwoord dat je kunt kiezen; dat is geen ja, dus ook geen vinkje.
                    const leeg = antwoord === "" || antwoord.toLowerCase() === "onbekend";
                    return (
                      <li
                        key={k.id}
                        className={
                          "fawkes-adv-preview__kenmerk" + (leeg ? " is-open" : nee ? " is-no" : " is-yes")
                        }
                      >
                        <span className="fawkes-adv-preview__kenmerk-mark" aria-hidden="true">
                          {leeg ? "–" : nee ? "✕" : "✓"}
                        </span>
                        <span className="fawkes-adv-preview__kenmerk-label">
                          {t(`advertentieKenmerk.${k.sleutel}`)}
                        </span>
                        <span className="fawkes-adv-preview__kenmerk-waarde">
                          {leeg ? t("advertentieIkzoekbaas.onbekend") : k.waarde}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="fawkes-adv-preview__blokleeg">{t("advertentieDetailView.bewerk.leeg")}</p>
              )}
            </section>
          )}

          {(data.basisLocatie || data.afzenderAdres || bewerk) && (
            <section className="fawkes-adv-preview__location">
              <span className="fawkes-adv-preview__location-icon" aria-hidden="true">📍</span>
              <div>
                {data.basisLocatie && <strong>{data.basisLocatie}</strong>}
                {data.afzenderAdres && <span className="fawkes-adv-preview__location-adres">{data.afzenderAdres}</span>}
                {!data.basisLocatie && !data.afzenderAdres && (
                  <span className="fawkes-adv-preview__blokleeg">{t("advertentieDetailView.bewerk.leeg")}</span>
                )}
              </div>
              {bewerk && (
                <BlokKnop titel={t("advertentieDetailView.bewerk.afzender")} onClick={bewerk.afzender} />
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** Het antwoord zonder de toelichting: "Nee, jachtinstinct" → "Nee". */
function antwoordVan(waarde: string | null): string {
  if (!waarde) return "";
  const komma = waarde.indexOf(",");
  return (komma === -1 ? waarde : waarde.slice(0, komma)).trim();
}

/** Een ISO-datum als "24 december 2024" — zo staat de geboortedatum op ikzoekbaas. */
function nlDatum(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
}

/** De toelichting achter het antwoord: "Nee, jachtinstinct" → "jachtinstinct". */
function toelichtingVan(waarde: string | null): string {
  if (!waarde) return "";
  const komma = waarde.indexOf(",");
  return komma === -1 ? "" : waarde.slice(komma + 1).trim();
}

/** One read-only label/value pair. Hidden entirely when the value is empty. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="fawkes-adv-detail__field">
      <span className="fawkes-adv-detail__label">{label}</span>
      <span className="fawkes-adv-detail__value">{value}</span>
    </div>
  );
}

// advertentie-editor-writes: the Advertentie detail, now editable for the editor core (non-AI,
// non-publish). Read mode shows the dossier snapshot + texts + kenmerken + afzender + publication + log;
// "Bewerken" turns the texts/alt-text/afzender into inputs and the kenmerken into status+publish
// controls. Saves via PUT /api/herplaatsing/{id} (optimistic-locked); "Markeer klaar voor controle"
// (CONCEPT only) posts to .../markeer-klaar behind the completeness gate (422 lists missing fields).
// ROLE_ADVERTENTIE_EDIT is enforced server-side (403 handled). Photo selection + AI + publishing stay
// on the Jmix screens (strangler).
export function AdvertentieDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<AdvertentieDetailData>({
    url: `herplaatsing/${id}`,
    method: "get",
  });
  const a = data?.data;

  const { mutate: put, isLoading: saving } = useCustomMutation();
  const { mutate: postKlaar, isLoading: marking } = useCustomMutation();

  const [edit, setEdit] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop");
  // Op welk platform deze vestiging publiceert (Instellingen → Advertentie). Bij BEIDE kiest de operator
  // zelf welke van de twee previews hij bekijkt.
  const { data: kanaalFeed } = useCustom<{ kanaal: Kanaal; contactTekst: string | null }>({
    url: "herplaatsing/kanaal", method: "get",
  });
  // De vragen met hun antwoorden, zoals ikzoekbaas ze stelt. De api is de enige plek waar die lijst
  // staat, zodat het invulscherm, de preview en het publiceren nooit uit elkaar lopen.
  const { data: vragenFeed } = useCustom<Vraag[]>({ url: "herplaatsing/kanaal/vragen", method: "get" });
  const vragen = useMemo(() => {
    const rijen = vragenFeed?.data;
    const kaart = new Map<string, Vraag>();
    if (Array.isArray(rijen)) rijen.forEach((v) => kaart.set(v.sleutel, v));
    return kaart;
  }, [vragenFeed]);
  const kanaal: Kanaal = kanaalFeed?.data?.kanaal ?? "DIERMATCH";
  const kanaalTeksten = { contact: kanaalFeed?.data?.contactTekst ?? null };
  // Bij "allebei" opent de preview op ikzoekbaas.nl: een vestiging die dat kanaal aanzet publiceert daar,
  // en DierMatch staat ernaast. Wat de operator daarna kiest onthouden we — anders klikt hij dat elke keer
  // opnieuw.
  const [previewKanaal, setPreviewKanaal] = useState<"DIERMATCH" | "IKZOEKBAAS" | null>(
    () => (globalThis.localStorage?.getItem("fawkes.advertentie.kanaal") as "DIERMATCH" | "IKZOEKBAAS" | null) ?? null,
  );
  // Welke sites er te kiezen zijn volgt de organisatie, niet het kanaalveld op de advertentie zelf.
  //
  // WAAROM. Dat veld zegt niet meer waar het dier staat -- dat staat per site in fapi_advertentie_site,
  // en die schakelaars hiernaast zijn de waarheid. Op de werkdatabase draagt elke advertentie
  // IKZOEKBAAS (1570 van de 1570), ook nu de organisatie op "allebei" staat. Lieten we dat veld
  // beslissen, dan toonde het scherm één schakelaar en geen keuze tussen de twee previews -- terwijl
  // het instelvenster belooft dat je mag kiezen welke van de twee je bekijkt. De organisatie bepaalt
  // dus wat er aangeboden wordt; de schakelaars bepalen waar het dier daadwerkelijk op staat.
  const advKanaal: Kanaal = kanaal;
  const getoondKanaal = advKanaal === "BEIDE"
    ? (previewKanaal ?? "IKZOEKBAAS")
    : (advKanaal === "IKZOEKBAAS" ? "IKZOEKBAAS" : "DIERMATCH");
  const [form, setForm] = useState<FormState | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // Staat er iets in de platformwachtrij, dan kijkt het scherm af en toe of het intussen aankwam: de
  // wachtrij probeert het op de achtergrond, en zonder dit bleef "nog niet op het platform" staan tot
  // iemand de pagina herlaadt. Niet tijdens bewerken, zodat een verversing geen formulier raakt.
  const wachtOpPlatform = a?.platformWachtrij?.toestand === "WACHTEND";
  useEffect(() => {
    if (!wachtOpPlatform || edit) return undefined;
    const timer = setInterval(() => {
      void refetch();
    }, 30_000);
    return () => clearInterval(timer);
  }, [wachtOpPlatform, edit, refetch]);

  // De foto's beheer je vanaf de foto zelf: een plusje op het beeld in de preview opent deze popup.
  const [fotosOpen, setFotosOpen] = useState(false);
  const [fotoBij, setFotoBij] = useState<number | null>(null);
  const [uploadFout, setUploadFout] = useState(false);
  const [uploadt, setUploadt] = useState(false);

  // De keuzefoto's voor de hoofdfoto: nodig zodra je bewerkt of de fotopopup opent.
  const { data: optiesData, refetch: refetchOpties } = useCustom<FotoOptie[]>({
    url: `herplaatsing/${id}/foto-opties`,
    method: "get",
    queryOptions: { enabled: edit || fotosOpen },
  });
  const fotoOpties = optiesData?.data ?? [];

  // De foto's uit Media & documenten voor de extra foto's; zelfde voorwaarde als hierboven.
  const { data: mediaPoolData, refetch: refetchPool } = useCustom<MediaPoolItem[]>({
    url: `herplaatsing/${id}/media-pool`,
    method: "get",
    queryOptions: { enabled: edit || fotosOpen },
  });
  const mediaPool = mediaPoolData?.data ?? [];
  const { mutate: putFotos, isLoading: fotoBusy } = useCustomMutation();
  const [fotoSel, setFotoSel] = useState<Set<number>>(new Set());
  // Seed the selection from the pool's current state (initial load + after a save refetch). Mid-edit
  // toggles change fotoSel (not the pool), so they persist until the next save.
  const poolKey = mediaPool.map((m) => `${m.animalFileId}:${m.inAdvertentie}`).join(",");
  useEffect(() => {
    setFotoSel(new Set(mediaPool.filter((m) => m.inAdvertentie).map((m) => m.animalFileId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey]);

  // The publish-guard checklist — fetched once the ad is loaded (the AI-assistent "Controleer
  // volledigheid" card reads it for any status, and the ready-for-review section reuses it).
  const { data: guardData, refetch: refetchGuard } = useCustom<PublishGuard>({
    url: `herplaatsing/${id}/publish-guard`,
    method: "get",
    queryOptions: { enabled: !!a },
  });
  const guard = guardData?.data;

  // Hoe vaak dit dier op de site bekeken is. Apart opgehaald en niet meegeleverd met de
  // advertentie: het cijfer komt van het platform, en een platform dat niet antwoordt mag hoogstens
  // dit ene getal kosten — niet het openen van de advertentie.
  const { data: weergavenData } = useCustom<{ weergaven: number | null }>({
    url: `herplaatsing/${id}/weergaven`,
    method: "get",
    queryOptions: { enabled: !!a },
  });
  const weergaven = weergavenData?.data?.weergaven ?? null;

  // Whether an AI provider is configured — gates the editor's AI buttons + the social-kit card.
  const { data: aiAvailData } = useCustom<{ available: boolean }>({
    url: "herplaatsing/ai-availability",
    method: "get",
  });
  const aiAvailable = aiAvailData?.data?.available === true;
  const { mutate: postAi, isLoading: aiBusy } = useCustomMutation();
  const [social, setSocial] = useState<Record<string, string> | null>(null);
  // Publicatiegegevens en het logboek zijn naslag, geen werkgebied: ze zitten achter een knop in
  // plaats van onder de advertentie, waar ze het scherm alleen langer maakten.
  const [historie, setHistorie] = useState(false);
  // De vragen van ikzoekbaas vul je in vanaf de plek waar je ze mist: de kenmerkentabel in de preview.
  // Bij annuleren gaan de antwoorden terug naar wat ze bij openen waren.
  // "Maak conceptekst" vraagt eerst wat alleen de verzorger weet; zonder die antwoorden schrijft de AI
  // een tekst die niets meer is dan de kenmerkentabel in zinnen.
  const [wizard, setWizard] = useState(false);
  const [privacy, setPrivacy] = useState<{ schoon: boolean; waarschuwingen: PrivacyWaarschuwing[] } | null>(null);
  const [openPunten, setOpenPunten] = useState(false);
  const [kenmerkenOpen, setKenmerkenOpen] = useState(false);
  // De bronzin per vraag, zolang dit scherm openstaat: waar de AI zijn antwoord op baseerde.
  const [aiBronnen, setAiBronnen] = useState<Record<string, string>>({});
  const [aiVragenBezig, setAiVragenBezig] = useState(false);
  const [kenmerkenBij, setKenmerkenBij] = useState<FormState["kenmerken"] | null>(null);
  /**
   * Welk veld er in de bewerkpopup staat.
   *
   * <p>Een knopje bij een blok zette eerst de bewerkmodus aan en sprong naar het juiste veld in het
   * formulier ernaast. Dat werkt, maar je kijkt dan ineens naar het hele formulier terwijl je één
   * regel kwam wijzigen. In een popup staat alleen datgene wat je aanklikte, en sluit hij weer zodra
   * je klaar bent -- de preview eronder blijft staan waar hij stond.
   *
   * <p>Deze twee staan bij de andere toestand en niet verderop bij hun eigen functies: hieronder
   * stopt dit scherm bij het laden met een eigen blok, en een hook na dat punt wordt bij de ene
   * tekenbeurt wel en bij de andere niet aangeroepen. React telt zijn hooks, en struikelt daarover
   * met "Rendered more hooks than during the previous render".
   */
  const [veldPopup, setVeldPopup] = useState<VeldPopup | null>(null);
  /** Het formulier zoals het bij openen was; Annuleren zet het daarop terug. */
  const [veldBij, setVeldBij] = useState<FormState | null>(null);

  const { mutate: postAction, isLoading: acting } = useCustomMutation();

  // Reseed the form whenever the loaded advertisement changes (initial load + after a save bumps version).
  useEffect(() => {
    if (a) setForm(seed(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id, a?.version]);

  // De privacycheck loopt vanzelf bij elke opgeslagen versie. Als knop drukte niemand hem in, terwijl
  // juist deze fout — een naam of telefoonnummer in de advertentietekst — niet gemaakt mag worden. Het
  // is een AI-aanroep, dus één keer per versie en niet bij elke toetsaanslag.
  useEffect(() => {
    const tekst = [a?.tekstOverDitDier, a?.tekstPastBij, a?.tekstBelangrijk]
      .filter((x) => x && x.trim())
      .join("\n\n");
    if (!a || !aiAvailable || !tekst) {
      setPrivacy(null);
      return;
    }
    postAi(
      {
        url: `herplaatsing/${id}/ai/privacycheck`,
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const d = res.data as { schoon?: boolean; waarschuwingen?: PrivacyWaarschuwing[] };
          setPrivacy({ schoon: !!d.schoon, waarschuwingen: d.waarschuwingen ?? [] });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id, a?.version, aiAvailable]);

  const back = (
    <div className="fawkes-adv-detail__bar">
      <Button variant="secondary" onClick={onBack}>
        ← {t("advertentieDetailView.back")}
      </Button>
    </div>
  );

  if (isLoading) {
    return (
      <FormCard>
        {back}
        <p className="fawkes-muted">{t("common.loading")}</p>
      </FormCard>
    );
  }
  if (isError || !a || !form) {
    return (
      <FormCard>
        {back}
        <p className="fawkes-error">{t("advertentieDetailView.loadFailed")}</p>
      </FormCard>
    );
  }

  // "Live preview" hoort ook echt live te zijn: terwijl je bewerkt kijkt de preview naar het formulier,
  // niet naar wat er is opgeslagen. Anders typ je een tekst en verandert er rechts niets.
  const getoond: AdvertentieDetailData = edit
    ? {
        ...a,
        tekstOverDitDier: form.tekstOverDitDier || null,
        tekstPastBij: form.tekstPastBij || null,
        tekstBelangrijk: form.tekstBelangrijk || null,
        tags: splitsTags(form.tags),
        succesverhaal: form.succesverhaal || null,
        altTekst: form.altTekst || null,
        afzenderNaam: form.afzenderNaam || null,
        afzenderTelefoon: form.afzenderTelefoon || null,
        afzenderEmail: form.afzenderEmail || null,
        afzenderUrl: form.afzenderUrl || null,
        buitenbeentje: form.buitenbeentje,
        hoofdfotoId: form.hoofdfotoId,
        kenmerken: form.kenmerken.map((k) => {
          const bron = a.kenmerken.find((x) => x.id === k.id);
          return { ...k, volgorde: bron?.volgorde ?? null };
        }),
      }
    : a;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const setKenmerk = (kid: string, patch: Partial<FormState["kenmerken"][number]>) =>
    setForm((f) =>
      f
        ? {
            ...f,
            kenmerken: f.kenmerken.map((k) => {
              if (k.id !== kid) return k;
              const next = { ...k, ...patch };
              // Publiceerbaar is only possible for a confirmed attribute (Jmix coupling).
              if (next.status !== "BEVESTIGD") next.publiceerbaar = false;
              return next;
            }),
          }
        : f,
    );

  const openFotos = () => {
    setFotoBij(form.hoofdfotoId);
    setFotosOpen(true);
  };

  /**
   * Foto's toevoegen vanuit de popup: ze gaan naar Media & documenten van het dier (daar horen ze
   * thuis), en verschijnen daarna in de keuzelijst hierboven. Met een telefoon opent dezelfde knop de
   * camera, want het invoerveld staat op capture.
   */
  const onUpload = (bestanden: FileList | null) => {
    if (!bestanden || bestanden.length === 0 || a.dierId == null) return;
    setUploadFout(false);
    setUploadt(true);
    uploadMedia(String(a.dierId), Array.from(bestanden), "", [])
      .then(() => Promise.all([refetchPool(), refetchOpties()]))
      .catch(() => setUploadFout(true))
      .finally(() => setUploadt(false));
  };

  /** Annuleren zet de hoofdfoto terug op wat hij bij het openen was. */
  const sluitFotos = () => {
    setForm((f) => (f ? { ...f, hoofdfotoId: fotoBij } : f));
    setFotosOpen(false);
  };

  const openKenmerken = () => {
    setKenmerkenBij(form.kenmerken);
    setKenmerkenOpen(true);
  };

  const openVeld = (welk: VeldPopup) => {
    setVeldBij(form);
    setVeldPopup(welk);
  };

  const sluitVeld = () => {
    if (veldBij) setForm(veldBij);
    setVeldBij(null);
    setVeldPopup(null);
  };

  /** Meteen bewaren: de popup is geen formulier dat je later nog een keer opslaat. */
  const bewaarVeld = () =>
    onSave(() => {
      setVeldBij(null);
      setVeldPopup(null);
    });

  /** Wat elk blok in de DierMatch-preview opent. */
  const previewBewerk: PreviewBewerk = {
    tekst: (veld) => openVeld({ soort: "tekst", veld }),
    tags: () => openVeld({ soort: "tags" }),
    kenmerken: openKenmerken,
    fotos: openFotos,
    afzender: () => openVeld({ soort: "afzender" }),
  };

  const sluitKenmerken = () => {
    if (kenmerkenBij) setForm((f) => (f ? { ...f, kenmerken: kenmerkenBij } : f));
    setKenmerkenBij(null);
    setKenmerkenOpen(false);
  };

  /**
   * De vragenlijst laten invullen door de AI. Het levert voorstellen: ze staan in het formulier maar
   * zijn pas opgeslagen als je op Opslaan drukt, met de zin uit het dossier ernaast om na te kijken.
   */
  const vulVragenMetAi = () => {
    setNotice(null);
    setAiVragenBezig(true);
    postAction(
      { url: `herplaatsing/${id}/ai/vragen`, method: "post", values: {},
        successNotification: false, errorNotification: false },
      {
        onSuccess: (res) => {
          setAiVragenBezig(false);
          const voorstellen = (res?.data as { voorstellen?: Array<{ sleutel: string; antwoord: string; bron?: string }> })
            ?.voorstellen ?? [];
          if (voorstellen.length === 0) {
            setNotice({ kind: "aiGeenVoorstellen" });
            return;
          }
          const bronnen: Record<string, string> = {};
          for (const v of voorstellen) if (v.bron) bronnen[v.sleutel] = v.bron;
          setAiBronnen(bronnen);
          setForm((f) => f && ({
            ...f,
            kenmerken: f.kenmerken.map((k) => {
              const voorstel = voorstellen.find((v) => v.sleutel === k.sleutel);
              // Een antwoord dat er al staat is van een mens; dat overschrijft de AI niet.
              if (!voorstel || (k.waarde && k.waarde.trim() !== "")) return k;
              return { ...k, waarde: voorstel.antwoord, status: "BEVESTIGD" };
            }),
          }));
        },
        onError: () => { setAiVragenBezig(false); setNotice({ kind: "aiFailed" }); },
      },
    );
  };

  const bewaar = (f: FormState, na?: () => void) => {
    setNotice(null);
    put(
      {
        url: `herplaatsing/${id}`,
        method: "put",
        values: {
          version: a.version,
          tekstOverDitDier: f.tekstOverDitDier,
          tekstPastBij: f.tekstPastBij,
          tekstBelangrijk: f.tekstBelangrijk,
          tags: splitsTags(f.tags),
          succesverhaal: f.succesverhaal,
          altTekst: f.altTekst,
          afzenderNaam: f.afzenderNaam,
          afzenderTelefoon: f.afzenderTelefoon,
          afzenderEmail: f.afzenderEmail,
          afzenderUrl: f.afzenderUrl,
          buitenbeentje: f.buitenbeentje,
          hoofdfotoId: f.hoofdfotoId,
          kenmerken: f.kenmerken
            .filter((k) => !k.id.startsWith("nieuw:") || (k.waarde != null && k.waarde !== ""))
            .map((k) => ({
              id: k.id,
              status: k.status,
              waarde: k.waarde,
              publiceerbaar: k.publiceerbaar,
            })),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setEdit(false);
          setNotice({ kind: "saved" });
          refetch();
          na?.();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice({ kind: code === 409 ? "conflict" : code === 403 ? "forbidden" : "error" });
        },
      },
    );
  };

  const onSave = (na?: () => void) => bewaar(form, na);

  const onMarkeerKlaar = () => {
    setNotice(null);
    postKlaar(
      {
        url: `herplaatsing/${id}/markeer-klaar`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setNotice({ kind: "klaar" });
          refetch();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          if (code === 422) {
            const body = err as {
              response?: { data?: { ontbrekendeVelden?: string[] } };
              ontbrekendeVelden?: string[];
            };
            const velden = body.response?.data?.ontbrekendeVelden ?? body.ontbrekendeVelden ?? [];
            setNotice({ kind: "onvolledig", velden });
            // Zonder velden zegt de melding niets; de puntenlijst laat zien wat er wél moet gebeuren.
            if (velden.length === 0) {
              void refetchGuard();
              setOpenPunten(true);
            }
          } else {
            setNotice({ kind: code === 403 ? "forbidden" : code === 409 ? "conflict" : "error" });
          }
        },
      },
    );
  };

  const onCancel = () => {
    setForm(seed(a));
    setEdit(false);
    setNotice(null);
  };

  // publiceren: approve / publish / lifecycle. Each POSTs to herplaatsing/{id}/<path>, refetches the
  // detail (+ the guard while ready-for-review) and surfaces a notice. Publish 422 lists the open reasons.
  /**
   * De twee standen die het overzicht ook zet: in het zonnetje en gereserveerd. Ze gaan buiten het
   * bewerkformulier om — het zijn losse endpoints die de advertentie meteen terugsturen — dus ze
   * werken ongeacht of je aan het bewerken bent, en ze pakken geen halfaf formulier mee.
   */
  /** Op welke site(s) dit dier komt te staan: DierMatch, ikzoekbaas of allebei. */
  /**
   * Dit dier op één site aan- of uitzetten. De andere site blijft zoals hij was.
   *
   * <p>Mislukt het, dan komen de redenen uit de publiceerguard terug ("er staat nog geen foto bij");
   * die horen in beeld, want dat is het antwoord op de vraag die de gebruiker stelt.
   */
  const zetSite = (site: "DIERMATCH" | "IKZOEKBAAS", aan: boolean) => {
    setNotice(null);
    postAction(
      {
        url: `herplaatsing/${id}/site`,
        method: "post",
        values: { site, aan },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { setNotice({ kind: "saved" }); refetch(); },
        onError: (fout) => {
          const body = (fout as { response?: { data?: { redenen?: string[]; message?: string } } })?.response?.data;
          setNotice(body?.redenen?.length ? { kind: "publishBlocked", redenen: body.redenen } : { kind: "error" });
        },
      },
    );
  };

  const runSnelActie = (stand: string | null) => {
    setNotice(null);
    postAction(
      {
        url: `herplaatsing/${id}/stand`,
        method: "post",
        values: { stand },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => { refetch(); }, onError: () => setNotice({ kind: "error" }) },
    );
  };

  const runAction = (path: string, okNotice: Notice) => {
    setNotice(null);
    const gestart = Date.now();
    postAction(
      {
        url: `herplaatsing/${id}/${path}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          // Publiceren, opnieuw versturen en de levenscyclus geven de advertentie terug: zegt de
          // platformwachtrij daarin dat het niet aankwam, dan is dat de uitkomst — niet "verstuurd".
          const wachtrij = (res?.data as Partial<AdvertentieDetailData> | undefined)?.platformWachtrij;
          setNotice(platformNotice(wachtrij, gestart) ?? okNotice);
          refetch();
          refetchGuard();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          if (path === "publiceer" && code === 422) {
            const redenen =
              (err as { response?: { data?: { redenen?: string[] } } }).response?.data?.redenen ?? [];
            setNotice({ kind: "publishBlocked", redenen });
          } else {
            setNotice({ kind: code === 403 ? "forbidden" : code === 409 ? "conflict" : "error" });
          }
        },
      },
    );
  };

  // ai: editor assist. Each POSTs to herplaatsing/{id}/ai/<x>; the proposal is applied client-side into
  // the edit form (non-destructive until the user saves). A 502 (no provider / call failed) → aiFailed.
  const callAi = (path: string, values: Record<string, unknown>, onOk: (data: unknown) => void) => {
    setNotice(null);
    postAi(
      { url: `herplaatsing/${id}/ai/${path}`, method: "post", values, successNotification: false, errorNotification: false },
      { onSuccess: (res) => onOk(res.data), onError: () => setNotice({ kind: "aiFailed" }) },
    );
  };

  const onAiConcept = (antwoorden: ConceptAntwoorden) =>
    callAi("concepttekst", { ...antwoorden }, (data) => {
      const d = data as { overDitDier?: string; pastBij?: string; belangrijkOmTeWeten?: string };
      const nieuw: FormState = {
        ...form,
        tekstOverDitDier: d.overDitDier ?? form.tekstOverDitDier,
        tekstPastBij: d.pastBij ?? form.tekstPastBij,
        tekstBelangrijk: d.belangrijkOmTeWeten ?? form.tekstBelangrijk,
      };
      setForm(nieuw);
      setWizard(false);
      // Meteen bewaren: de tekst hoort in de advertentie te staan, niet alleen in een formulier dat je
      // ook per ongeluk kunt weggooien. Daarna staat hij open om bij te schaven.
      bewaar(nieuw, () => setEdit(true));
    });

  /** De drie tekstblokken, elk met hun veld in het formulier — zo hoeft de AI-hulp niet drie keer. */
  const BLOKKEN = [
    { sleutel: "overDitDier", veld: "tekstOverDitDier" as const, label: t("advertentieDetailView.tekst.overDitDier") },
    { sleutel: "pastBij", veld: "tekstPastBij" as const, label: t("advertentieDetailView.tekst.pastBij") },
    { sleutel: "belangrijkOmTeWeten", veld: "tekstBelangrijk" as const, label: t("advertentieDetailView.tekst.belangrijk") },
  ];

  /** Eén blok opnieuw laten schrijven; de andere twee gaan mee als context zodat het erop aansluit. */
  const onBlokOpnieuw = (blok: string, veld: keyof FormState) =>
    callAi(
      "concepttekst",
      {
        blok,
        bestaandeTekst: BLOKKEN.filter((b) => b.veld !== veld)
          .map((b) => `${b.label}: ${form[b.veld] as string}`)
          .filter((r) => !r.endsWith(": "))
          .join("\n\n"),
      },
      (data) => {
        const d = data as Record<string, string | undefined>;
        const voorstel = d[blok];
        if (voorstel) setForm((f) => (f ? { ...f, [veld]: voorstel } : f));
        setNotice({ kind: "aiApplied" });
      },
    );

  /** Hetzelfde blok, maar dan de bestaande tekst herschreven (korter en vriendelijker). */
  const onBlokVerbeter = (label: string, veld: keyof FormState) =>
    callAi("verbeter", { blokNaam: label, huidigeTekst: form[veld] as string }, (data) => {
      const d = data as { voorstel?: string };
      if (d.voorstel) setForm((f) => (f ? { ...f, [veld]: d.voorstel as string } : f));
      setNotice({ kind: "aiApplied" });
    });

  /**
   * De privacycheck draait vanzelf zodra er een opgeslagen tekst is: hem als knop aanbieden betekende
   * dat niemand hem indrukte, terwijl juist déze fout (een naam of telefoonnummer in de tekst) niet
   * gemaakt mag worden. Eén keer per opgeslagen versie — het is een AI-aanroep, geen gratis controle.
   */
  const onAiPrivacy = (stil = false) => {
    const tekst = [a.tekstOverDitDier, a.tekstPastBij, a.tekstBelangrijk]
      .filter((s) => s && s.trim())
      .join("\n\n");
    if (!tekst) {
      setPrivacy(null);
      return;
    }
    postAi(
      {
        url: `herplaatsing/${id}/ai/privacycheck`,
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const d = res.data as { schoon?: boolean; waarschuwingen?: PrivacyWaarschuwing[] };
          setPrivacy({ schoon: !!d.schoon, waarschuwingen: d.waarschuwingen ?? [] });
          if (!stil) {
            setNotice(d.schoon
              ? { kind: "aiPrivacyClean" }
              : { kind: "aiPrivacyWarn", warnings: d.waarschuwingen ?? [] });
          }
        },
        onError: () => { if (!stil) setNotice({ kind: "aiFailed" }); },
      },
    );
  };

  const onAiSocial = () =>
    callAi("social", {}, (data) => setSocial(data as Record<string, string>));

  const onAiAltTekst = () =>
    callAi("alt-tekst", {}, (data) => {
      const d = data as { altTekst?: string };
      if (d.altTekst) setForm((f) => (f ? { ...f, altTekst: d.altTekst as string } : f));
      setNotice({ kind: "aiApplied" });
    });

  // media-picker: save the extra-photo selection (the chosen animalFileIds in pool order) → PUT, refetch.
  const toggleFoto = (animalFileId: number) =>
    setFotoSel((s) => {
      const next = new Set(s);
      if (next.has(animalFileId)) next.delete(animalFileId);
      else next.add(animalFileId);
      return next;
    });
  const onSaveFotos = () => {
    setNotice(null);
    const animalFileIds = mediaPool.filter((m) => fotoSel.has(m.animalFileId)).map((m) => m.animalFileId);
    putFotos(
      {
        url: `herplaatsing/${id}/extra-fotos`,
        method: "put",
        values: { animalFileIds },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setNotice({ kind: "saved" });
          refetch();
          refetchPool();
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice({ kind: code === 403 ? "forbidden" : "error" });
        },
      },
    );
  };

  const logColumns: Column<LogEntry>[] = [
    { header: t("advertentieDetailView.log.column.tijd"), cell: (l) => formatDateTime(l.tijd, locale) },
    { header: t("advertentieDetailView.log.column.actor"), cell: (l) => t(`advertentieLogActor.${l.actor}`) },
    { header: t("advertentieDetailView.log.column.actie"), cell: (l) => l.actie ?? "—" },
    { header: t("advertentieDetailView.log.column.detail"), cell: (l) => l.detail ?? "—" },
  ];

  const busy = saving || marking || acting || aiBusy || fotoBusy;

  return (
    <div className="fawkes-adv-detail">
      <div className="fawkes-adv-detail__topbar">
        {back}
        {/* De stand van deze advertentie hoort bovenaan, naast de terugknop: dat is wat je als eerste
            wilt weten als je hem opent. In de previewkop verdrongen ze de knoppen voor desktop,
            mobiel en Instagram naar een tweede regel. */}
        <div className="fawkes-adv-detail__stand">
            <StatusBadge tone={statusTone(a.status)}>{t(`advertentieStatus.${a.status}`)}</StatusBadge>
              {/* De twee controles waren knoppen die niemand indrukte; ze lopen nu vanzelf en staan hier.
                  Klikken doet ze opnieuw en zet de details in de melding erboven. */}
              {guard && (
                <button
                  type="button"
                  className="fawkes-adv-check"
                  onClick={() => { void refetchGuard(); setOpenPunten(true); }}
                  disabled={busy}
                >
                  <StatusBadge tone={guard.publiceerbaar ? "success" : "warning"}>
                    {guard.publiceerbaar
                      ? t("advertentieDetailView.check.compleet")
                      : t("advertentieDetailView.check.open", { args: [guard.redenen.length] })}
                  </StatusBadge>
                </button>
              )}
              {/* Hoe vaak er echt naar dit dier gekeken is. Naast de andere standen, want het is
                  dezelfde soort informatie: iets wat je wilt weten voordat je aan de tekst gaat
                  sleutelen. Niets bij null — "niet gepubliceerd" is geen 0. */}
              {weergaven != null && (
                <span className="fawkes-adv-bekeken" title={t("advertentieDetailView.bekeken.uitleg")}>
                  <EyeIcon width={14} height={14} aria-hidden="true" />
                  {t("advertentieDetailView.bekeken.label")}: <b>{weergaven.toLocaleString(locale)}</b>
                </span>
              )}
              {aiAvailable && privacy && (
                <button type="button" className="fawkes-adv-check" onClick={() => onAiPrivacy()} disabled={busy}>
                  <StatusBadge tone={privacy.schoon ? "success" : "warning"}>
                    {privacy.schoon
                      ? t("advertentieDetailView.check.privacySchoon")
                      : t("advertentieDetailView.check.privacyLet", { args: [privacy.waarschuwingen.length] })}
                  </StatusBadge>
                </button>
              )}
              {a.verouderd && (
                <StatusBadge tone="warning">{t("advertentieListView.verouderd.yes")}</StatusBadge>
              )}
        </div>
        <div className="fawkes-adv-detail__actions">
          {edit ? (
            <>
              <Button variant="secondary" onClick={onCancel} disabled={busy}>
                {t("advertentieDetailView.cancel")}
              </Button>
              <Button variant="primary" onClick={() => onSave()} disabled={busy}>
                {t("advertentieDetailView.save")}
              </Button>
            </>
          ) : (
            <>
              {a.status === "CONCEPT" && (
                <Button variant="secondary" onClick={onMarkeerKlaar} disabled={busy}>
                  {t("advertentieDetailView.markeerKlaar")}
                </Button>
              )}
              {a.status === "KLAAR_VOOR_CONTROLE" && (
                <>
                  <Button variant="secondary" onClick={() => runAction("goedkeuren", { kind: "approved" })} disabled={busy}>
                    {t("advertentieDetailView.goedkeuren")}
                  </Button>
                  <Button variant="primary" onClick={() => runAction("publiceer", { kind: "published" })} disabled={busy}>
                    {t("advertentieDetailView.publiceer")}
                  </Button>
                </>
              )}
              {a.status === "GEPUBLICEERD" && (
                <>
                  {/* Opnieuw versturen is er voor als het pakket verandert maar de advertentie niet:
                      een nieuw veld of een gewijzigde kanaalkeuze bereikt het platform pas bij de
                      eerstvolgende publicatie, en een gepubliceerde advertentie heeft geen
                      publiceerknop meer. Zonder deze knop is er dan geen weg meer heen. */}
                  <Button
                    variant="secondary"
                    onClick={() => runAction("opnieuw-versturen", { kind: "opnieuwVerstuurd" })}
                    disabled={busy}
                    title={t("advertentieDetailView.opnieuwVersturenUitleg")}
                  >
                    {t("advertentieDetailView.opnieuwVersturen")}
                  </Button>
                  <Button variant="secondary" onClick={() => runAction("pauzeer", { kind: "lifecycle" })} disabled={busy}>
                    {t("advertentieDetailView.pauzeer")}
                  </Button>
                  <Button variant="secondary" onClick={() => runAction("markeer-verlopen", { kind: "lifecycle" })} disabled={busy}>
                    {t("advertentieDetailView.markeerVerlopen")}
                  </Button>
                </>
              )}
              {a.status === "GEPAUZEERD" && (
                <>
                  <Button variant="primary" onClick={() => runAction("hervat", { kind: "lifecycle" })} disabled={busy}>
                    {t("advertentieDetailView.hervat")}
                  </Button>
                  <Button variant="secondary" onClick={() => runAction("markeer-verlopen", { kind: "lifecycle" })} disabled={busy}>
                    {t("advertentieDetailView.markeerVerlopen")}
                  </Button>
                </>
              )}
              {a.status === "VERLOPEN" && (
                <Button variant="primary" onClick={() => runAction("heropen", { kind: "lifecycle" })} disabled={busy}>
                  {t("advertentieDetailView.heropen")}
                </Button>
              )}
              <Button variant="secondary" onClick={openKenmerken} disabled={busy}>
                {t("advertentieDetailView.kenmerkenInvullen")}
              </Button>
              <Button variant="secondary" onClick={() => setHistorie(true)}>
                {t("advertentieDetailView.historie")}
              </Button>
              <Button variant="secondary" onClick={() => setEdit(true)} disabled={busy}>
                {t("advertentieDetailView.edit")}
              </Button>
            </>
          )}
        </div>
      </div>

      {notice && (
        <p
          className={
            "fawkes-adv-banner " +
            (notice.kind === "saved" ||
            notice.kind === "klaar" ||
            notice.kind === "approved" ||
            notice.kind === "published" ||
            notice.kind === "lifecycle" ||
            notice.kind === "opnieuwVerstuurd" ||
            notice.kind === "aiApplied" ||
            notice.kind === "aiPrivacyClean"
              ? "fawkes-adv-banner--ok"
              : notice.kind === "onvolledig" ||
                  notice.kind === "publishBlocked" ||
                  notice.kind === "platformInWachtrij" ||
                  notice.kind === "aiPrivacyWarn"
                ? "fawkes-adv-banner--warn"
                : "fawkes-adv-banner--error")
          }
          role="status"
        >
          {notice.kind === "onvolledig"
            ? (notice.velden.length > 0
                ? `${t("advertentieDetailView.notice.onvolledig")} ${notice.velden.join(", ")}`
                : t("advertentieDetailView.notice.onvolledigLeeg"))
            : notice.kind === "publishBlocked"
              ? `${t("advertentieDetailView.notice.publishBlocked")} ${notice.redenen.join(" ")}`
              : notice.kind === "platformAfgewezen"
                ? `${t("advertentieDetailView.notice.platformAfgewezen")} ${notice.fout ?? ""}`.trim()
              : notice.kind === "aiPrivacyWarn"
                ? `${t("advertentieDetailView.notice.aiPrivacyWarn")} ${notice.warnings
                    .map((w) => `${w.type}: "${w.fragment}"`)
                    .join("; ")}`
                : t(`advertentieDetailView.notice.${notice.kind}`)}
        </p>
      )}

      {/* Alleen wat iets zegt: de naam stond hier een tweede keer en die balk is daarom weg
          (maintainer: "deze balk heeft geen toegevoegde waarde"). De waarschuwingen blijven. */}
      {a.verouderd && (
        <p className="fawkes-adv-banner fawkes-adv-banner--warn" role="status">
          {t("advertentieDetailView.verouderdBanner")}
        </p>
      )}
      {a.publicatieBlokkade && (
        <p className="fawkes-adv-banner fawkes-adv-banner--error" role="status">
          {t("advertentieDetailView.blokkadeBanner")}
          {a.publicatieBlokkadeReden ? ` — ${a.publicatieBlokkadeReden}` : ""}
        </p>
      )}
      {/* Het dier is de opvang uit en staat nog ergens op. Dat gaat 's nachts vanzelf weg, maar
          alleen bij opvangen die "automatisch van de site halen" aan hebben staan; staat die uit,
          dan bleef dit stil (Maarten, 02-11-2026). Alleen tonen als hij érgens op staat: een
          vertrokken dier met een offline advertentie is gewoon een afgeronde advertentie. */}
      {a.vertrokkenOp && Object.values(a.sites ?? {}).some(Boolean) && (
        <p className="fawkes-adv-banner fawkes-adv-banner--warn" role="status"
          data-testid="dier-weg-banner">
          {t("advertentieDetailView.dierWegBanner", { args: [nlDatum(a.vertrokkenOp) ?? a.vertrokkenOp] })}
        </p>
      )}
      <PlatformWachtrijBanner
        wachtrij={a.platformWachtrij}
        hersteladvies={a.hersteladvies}
        busy={busy}
        onOpnieuw={() => runAction("opnieuw-versturen", { kind: "opnieuwVerstuurd" })}
      />

      {/* Live preview + AI-assistent (acc parity): DierMatch-branded preview left, assistant panel right. */}
      <div className="fawkes-adv-editor-row">
        <FormCard>
          <div className="fawkes-adv-detail__preview-head">
            <SectionHeader>{t("advertentieDetailView.preview.section")}</SectionHeader>
            {/* Op welke sites dit dier staat. Twee schakelaars en geen driekeuze: de sites staan los
                van elkaar, en "allebei" was een derde waarde die dat verhulde. Aanzetten mag ook vanuit
                concept — de controlestap is een werkafspraak, geen slagboom. */}
            <div className="fawkes-adv-detail__sites" role="group"
              aria-label={t("advertentieDetailView.kanaal.label")}>
              {sitesVan(advKanaal).map((k) => {
                const staatErop = a.sites?.[k] ?? false;
                const naam = t(`advertentieDetailView.kanaal.${k}`);
                return (
                  <label key={k} className={"adv-site" + (staatErop ? " adv-site--aan" : "")}>
                    <span className="adv-site__naam">{naam}</span>
                    <span className="fui-toggle">
                      <input
                        type="checkbox" role="switch" className="fui-toggle__input"
                        data-testid={`site-schakelaar-${k}`}
                        checked={staatErop} disabled={busy}
                        aria-label={`${naam}: ${t(staatErop ? "advertentieDetailView.site.aan" : "advertentieDetailView.site.uit")}`}
                        onChange={(e) => zetSite(k, e.target.checked)}
                      />
                      <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
                    </span>
                    <span className="adv-site__stand">
                      {t(staatErop ? "advertentieDetailView.site.aan" : "advertentieDetailView.site.uit")}
                    </span>
                  </label>
                );
              })}
            </div>
            {advKanaal === "BEIDE" && (
              /* Staat hij op allebei: dan hoort de preview te laten kiezen welke je bekijkt. */
              <div className="fawkes-adv-detail__preview-devices" role="group"
                aria-label={t("advertentieDetailView.preview.kanaal")}>
                {(["DIERMATCH", "IKZOEKBAAS"] as const).map((k) => (
                  <button key={k} type="button"
                    className={"fawkes-adv-detail__device" + (getoondKanaal === k ? " fawkes-adv-detail__device--active" : "")}
                    aria-pressed={getoondKanaal === k}
                    onClick={() => {
                      setPreviewKanaal(k);
                      globalThis.localStorage?.setItem("fawkes.advertentie.kanaal", k);
                    }}>
                    {t(`advertentieDetailView.preview.kanaal.${k}`)}
                  </button>
                ))}
              </div>
            )}
            <div className="fawkes-adv-detail__preview-devices" role="group"
              aria-label={t("advertentieDetailView.preview.section")}>
              {(["desktop", "mobile", "instagram"] as PreviewDevice[]).map((d) => (
                <button key={d} type="button"
                  className={"fawkes-adv-detail__device" + (previewDevice === d ? " fawkes-adv-detail__device--active" : "")}
                  aria-pressed={previewDevice === d}
                  onClick={() => setPreviewDevice(d)}>
                  {t(`advertentieDetailView.preview.device.${d}`)}
                </button>
              ))}
            </div>
          </div>
          {getoondKanaal === "IKZOEKBAAS"
            ? <AdvertentiePreviewIkZoekBaas advertentieId={id} data={getoond} device={previewDevice}
                kanaalTeksten={kanaalTeksten} onKenmerken={openKenmerken} onFotos={openFotos} />
            : <AdvertentiePreview advertentieId={id} data={getoond} device={previewDevice}
                bewerk={previewBewerk} />}
        </FormCard>

        <div className="fawkes-adv-editor-kolom">
        <aside className="fawkes-adv-ai" aria-label={t("advertentieDetailView.ai.panel.title")}>
          <div className="fawkes-adv-ai__head">
            <span className="fawkes-adv-ai__title">{t("advertentieDetailView.ai.panel.title")}</span>
            <p className="fawkes-adv-ai__subtitle">{t("advertentieDetailView.ai.panel.subtitle")}</p>
          </div>
          <div className="fawkes-adv-ai__cards">
            {[
              { key: "concept", onClick: () => setWizard(true), ai: true },
              { key: "warmer", onClick: () => onBlokVerbeter(BLOKKEN[0].label, "tekstOverDitDier"), ai: true },
              { key: "korter", onClick: () => onBlokVerbeter(BLOKKEN[0].label, "tekstOverDitDier"), ai: true },
              { key: "social", onClick: onAiSocial, ai: true },
              { key: "foto", onClick: onAiAltTekst, ai: true },
            ].map((c) => (
              <button key={c.key} type="button" className="fawkes-adv-ai__card"
                onClick={c.onClick} disabled={busy || (c.ai && !aiAvailable)}>
                <span className="fawkes-adv-ai__card-title">{t(`advertentieDetailView.ai.card.${c.key}.title`)}</span>
                <span className="fawkes-adv-ai__card-desc">{t(`advertentieDetailView.ai.card.${c.key}.desc`)}</span>
              </button>
            ))}
          </div>
          {/* Onder de AI-kaarten, maar er niet van: dit zijn de twee standen die je vanaf het
              overzicht ook al met één klik omzet. Ze staan hier zodat je ze niet kwijt bent zodra je
              de advertentie hebt geopend — en ze bewerken buiten het formulier om, dus ze werken ook
              als je niet in bewerkmodus staat. */}
          {a && (
            <div className="fawkes-adv-ai__snel">
              <span className="fawkes-adv-ai__snel-titel">{t("advertentieDetailView.snel.title")}</span>
              <AdvertentieSnelActies
                buitenbeentje={a.buitenbeentje}
                beschikbaarheid={a.basisBeschikbaarheid}
                status={a.status}
                busy={busy}
                onStand={runSnelActie}
              />
            </div>
          )}
        </aside>

        {/* Bewerken gebeurt naast de preview in plaats van eronder: zo zie je meteen wat je typt,
            en blijft het scherm één geheel in plaats van een rij blokken onder de advertentie. */}
        {edit && (
        <FormCard>
          <SectionHeader>{t("advertentieDetailView.section.teksten")}</SectionHeader>
          <div className="fawkes-adv-ai">
              {aiAvailable ? (
                <>
                  <span className="fawkes-adv-ai__label">{t("advertentieDetailView.ai.label")}</span>
                  <Button variant="secondary" onClick={() => setWizard(true)} disabled={busy}>
                    {t("advertentieDetailView.ai.concept")}
                  </Button>
                </>
              ) : (
                <span className="fawkes-muted">{t("advertentieDetailView.ai.unavailable")}</span>
            )}
          </div>
          <div className="fawkes-adv-detail__form">
              {BLOKKEN.map((b) => (
                <div key={b.sleutel} className="fawkes-adv-field">
                  <div className="fawkes-adv-blok-kop">
                    <span className="fawkes-adv-field__label">{b.label}</span>
                    {aiAvailable && (
                      <span className="fawkes-adv-blok-knoppen">
                        <button type="button" className="fawkes-adv-ai__inline"
                          onClick={() => onBlokOpnieuw(b.sleutel, b.veld)} disabled={busy}>
                          {t("advertentieDetailView.blok.opnieuw")}
                        </button>
                        <button type="button" className="fawkes-adv-ai__inline"
                          onClick={() => onBlokVerbeter(b.label, b.veld)}
                          disabled={busy || !(form[b.veld] as string)}>
                          {t("advertentieDetailView.blok.verbeter")}
                        </button>
                      </span>
                    )}
                  </div>
                  <textarea
                    className="fui-field__input fawkes-adv-textarea"
                    aria-label={b.label}
                    value={form[b.veld] as string}
                    onChange={(e) => set(b.veld, e.target.value)}
                  />
                </div>
              ))}
              {/* Alleen tonen als DierMatch in beeld is: ikzoekbaas doet niets met tags, en een veld
                  dat nergens heen gaat leert mensen het in te vullen voor niets. */}
              {kanaal !== "IKZOEKBAAS" && (
                <label className="fawkes-adv-field">
                  <span className="fawkes-adv-field__label">{t("advertentieDetailView.field.tags")}</span>
                  <input
                    className="fui-field__input"
                    value={form.tags}
                    onChange={(e) => set("tags", e.target.value)}
                    placeholder={t("advertentieDetailView.field.tagsPlaceholder")}
                  />
                  <small className="fawkes-muted">{t("advertentieDetailView.field.tagsHint")}</small>
                </label>
              )}
              {(a.status === "GEPLAATST" || form.succesverhaal) && (
                <div className="fawkes-adv-field">
                  <span className="fawkes-adv-field__label">
                    {t("advertentieDetailView.field.succesverhaal")}
                  </span>
                  <textarea
                    className="fui-field__input fawkes-adv-textarea"
                    aria-label={t("advertentieDetailView.field.succesverhaal")}
                    value={form.succesverhaal}
                    onChange={(e) => set("succesverhaal", e.target.value)}
                  />
                  <small className="fawkes-muted">{t("advertentieDetailView.field.succesverhaalHint")}</small>
                </div>
              )}
              <label className="fawkes-adv-field">
                <span className="fawkes-adv-field__label">{t("advertentieDetailView.field.altTekst")}</span>
                <input
                  className="fui-field__input"
                  maxLength={500}
                  value={form.altTekst}
                  onChange={(e) => set("altTekst", e.target.value)}
                />
                {aiAvailable && (
                  <button type="button" className="fawkes-adv-ai__inline" onClick={onAiAltTekst} disabled={busy}>
                    {t("advertentieDetailView.ai.altTekst")}
                  </button>
                )}
            </label>
          </div>
        </FormCard>
        )}

        {/* De afzender staat onderaan de advertentie, bij het adres, en was het enige blok zonder
            invulveld: de gegevens werden wel meebewaard, maar je kon ze alleen in het dossier wijzigen.
            Met een knopje bij dat blok hoort er ook een plek te zijn om terecht te komen. */}
        {edit && (
        <FormCard>
          <SectionHeader>{t("advertentieDetailView.section.afzender")}</SectionHeader>
          <div className="fawkes-adv-detail__form">
            {([
              { veld: "afzenderNaam" as const, label: t("advertentieDetailView.field.afzenderNaam") },
              { veld: "afzenderTelefoon" as const, label: t("advertentieDetailView.field.afzenderTelefoon") },
              { veld: "afzenderEmail" as const, label: t("advertentieDetailView.field.afzenderEmail") },
              { veld: "afzenderUrl" as const, label: t("advertentieDetailView.field.afzenderUrl") },
            ]).map((f) => (
              <label key={f.veld} className="fawkes-adv-field">
                <span className="fawkes-adv-field__label">{f.label}</span>
                <input
                  className="fui-field__input"
                  value={form[f.veld]}
                  onChange={(e) => set(f.veld, e.target.value)}
                />
              </label>
            ))}
            <small className="fawkes-muted">{t("advertentieDetailView.field.afzenderHint")}</small>
          </div>
        </FormCard>
        )}

        {/* Een redactiekeuze, geen eigenschap van het dier: hoort het in de rij "De buitenbeentjes"
            op de home van ikzoekbaas? Drie standen, want "niet ingesteld" is geen "nee" — dan leidt
            de site het zelf af uit de zorgvraag. */}
        {edit && (
        <FormCard>
          <SectionHeader>{t("advertentieDetailView.section.plaatsing")}</SectionHeader>
          <div className="fawkes-adv-field">
            <span className="fawkes-adv-field__label">
              {t("advertentieDetailView.buitenbeentje.label")}
            </span>
            <div className="fawkes-adv-keuze" role="group"
              aria-label={t("advertentieDetailView.buitenbeentje.label")}>
              {([
                { waarde: null, sleutel: "auto" },
                { waarde: true, sleutel: "wel" },
                { waarde: false, sleutel: "niet" },
              ] as const).map((o) => (
                <button key={o.sleutel} type="button"
                  className={"fawkes-adv-keuze__optie"
                    + (form.buitenbeentje === o.waarde ? " fawkes-adv-keuze__optie--actief" : "")}
                  aria-pressed={form.buitenbeentje === o.waarde}
                  onClick={() => set("buitenbeentje", o.waarde)}>
                  {t(`advertentieDetailView.buitenbeentje.${o.sleutel}`)}
                </button>
              ))}
            </div>
            <p className="fawkes-muted">
              {t(`advertentieDetailView.buitenbeentje.uitleg.${
                form.buitenbeentje === null ? "auto" : form.buitenbeentje ? "wel" : "niet"}`)}
            </p>
          </div>
        </FormCard>
        )}

      </div>
      </div>

      {a.status === "KLAAR_VOOR_CONTROLE" && guard && (
        <FormCard>
          <SectionHeader>{t("advertentieDetailView.section.publishGuard")}</SectionHeader>
          {guard.publiceerbaar ? (
            <p className="fawkes-adv-banner fawkes-adv-banner--ok" role="status">
              {t("advertentieDetailView.publishGuard.ok")}
            </p>
          ) : (
            <ul className="fawkes-adv-guard">
              {guard.redenen.map((reden, i) => (
                <li key={i} className="fawkes-adv-guard__item">
                  {reden}
                </li>
              ))}
            </ul>
          )}
        </FormCard>
      )}

      {social && (
        <Modal
          open
          title={t("advertentieDetailView.section.social")}
          onClose={() => setSocial(null)}
          footer={
            <Button variant="secondary" onClick={() => setSocial(null)}>
              {t("common.close")}
            </Button>
          }
        >
          <div className="fawkes-adv-social">
            {(["facebook", "instagram", "whatsapp", "nieuwsbrief"] as const).map((kanaal) => (
              <div key={kanaal} className="fawkes-adv-social__item">
                <div className="fawkes-adv-social__head">
                  <span className="fawkes-adv-social__kanaal">
                    {t(`advertentieDetailView.social.${kanaal}`)}
                  </span>
                  <button
                    type="button"
                    className="fawkes-adv-ai__inline"
                    onClick={() => void navigator.clipboard?.writeText(social[kanaal] ?? "")}
                  >
                    {t("advertentieDetailView.social.kopieer")}
                  </button>
                </div>
                <p className="fawkes-adv-social__text">{social[kanaal] || "—"}</p>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {wizard && (
        <AdvertentieConceptWizard
          naam={a.basisNaam ?? ""}
          heeftTekst={!!(form.tekstOverDitDier || form.tekstPastBij || form.tekstBelangrijk)}
          bezig={aiBusy || saving}
          onSluit={() => setWizard(false)}
          onSchrijf={onAiConcept}
        />
      )}

      {openPunten && guard && (
        <Modal
          open
          title={t("advertentieDetailView.punten.titel")}
          onClose={() => setOpenPunten(false)}
          footer={
            <Button variant="secondary" onClick={() => setOpenPunten(false)}>
              {t("common.close")}
            </Button>
          }
        >
          {guard.publiceerbaar ? (
            <p className="fawkes-adv-banner fawkes-adv-banner--ok" role="status">
              {t("advertentieDetailView.publishGuard.ok")}
            </p>
          ) : (
            <ul className="fawkes-adv-punten">
              {(guard.punten ?? guard.redenen.map((r) => ({ code: "", tekst: r, detail: null }))).map((punt, i) => (
                <li key={i} className="fawkes-adv-punt">
                  <span className="fawkes-adv-punt__wat">
                    <strong>{t(`advertentieDetailView.punt.${punt.code || "OVERIG"}`)}</strong>
                    <span>{punt.tekst}</span>
                  </span>
                  {punt.code === "STATUS" && a.status === "CONCEPT" && (
                    <Button variant="secondary" onClick={() => { setOpenPunten(false); onMarkeerKlaar(); }} disabled={busy}>
                      {t("advertentieDetailView.markeerKlaar")}
                    </Button>
                  )}
                  {(punt.code === "VELDEN" || punt.code === "PERSOONSGEGEVENS") && (
                    <Button variant="secondary" onClick={() => { setOpenPunten(false); setEdit(true); }} disabled={busy}>
                      {t("advertentieDetailView.edit")}
                    </Button>
                  )}
                  {punt.code === "GOEDKEURING" && a.status === "KLAAR_VOOR_CONTROLE" && (
                    <Button variant="secondary"
                      onClick={() => { setOpenPunten(false); runAction("goedkeuren", { kind: "approved" }); }}
                      disabled={busy}>
                      {t("advertentieDetailView.goedkeuren")}
                    </Button>
                  )}
                  {punt.code === "KENMERKEN" && (
                    <Button variant="secondary" onClick={() => { setOpenPunten(false); openKenmerken(); }} disabled={busy}>
                      {t("advertentieDetailView.kenmerkenInvullen")}
                    </Button>
                  )}
                  {punt.code === "PRIVACY" && (
                    <Button variant="secondary" onClick={() => { setOpenPunten(false); onAiPrivacy(); }} disabled={busy}>
                      {t("advertentieDetailView.punt.opnieuw")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}

      {fotosOpen && (
        <Modal
          open
          size="wide"
          title={t("advertentieDetailView.section.fotos")}
          onClose={sluitFotos}
          footer={
            <>
              <Button variant="secondary" onClick={sluitFotos} disabled={busy}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" disabled={busy}
                onClick={() => bewaar(form, () => { setFotoBij(null); setFotosOpen(false); })}>
                {t("common.save")}
              </Button>
            </>
          }
        >
      {a.dierId != null && (
        <div className="fawkes-adv-foto-upload">
          <label className="fawkes-adv-foto-upload__knop">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              disabled={uploadt || busy}
              onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
            />
            {uploadt ? t("advertentieDetailView.foto.uploadt") : t("advertentieDetailView.foto.toevoegen")}
          </label>
          <span className="fawkes-muted">{t("advertentieDetailView.foto.uploadHint")}</span>
          {uploadFout && (
            <p className="fawkes-adv-banner fawkes-adv-banner--error" role="alert">
              {t("advertentieDetailView.foto.uploadFout")}
            </p>
          )}
        </div>
      )}

      <div className="fawkes-adv-foto-block">
        <h4 className="fawkes-adv-detail__blok-title">{t("advertentieDetailView.hoofdfoto")}</h4>
        <div
              className="fawkes-adv-foto-grid"
              role="radiogroup"
              aria-label={t("advertentieDetailView.hoofdfoto")}
            >
              <button
                type="button"
                className={
                  "fawkes-adv-foto-choice" +
                  (form.hoofdfotoId == null ? " fawkes-adv-foto-choice--selected" : "")
                }
                aria-pressed={form.hoofdfotoId == null}
                onClick={() => set("hoofdfotoId", null)}
              >
                <span className="fawkes-adv-foto__img fawkes-adv-foto__img--none" aria-hidden="true" />
                <span className="fawkes-adv-foto-choice__label">
                  {t("advertentieDetailView.hoofdfoto.geen")}
                </span>
              </button>
              {fotoOpties.map((o) => (
                <button
                  key={o.afbeeldingId}
                  type="button"
                  className={
                    "fawkes-adv-foto-choice" +
                    (form.hoofdfotoId === o.afbeeldingId ? " fawkes-adv-foto-choice--selected" : "")
                  }
                  aria-pressed={form.hoofdfotoId === o.afbeeldingId}
                  onClick={() => set("hoofdfotoId", o.afbeeldingId)}
                >
                  <AuthedThumb advertentieId={id} afbeeldingId={o.afbeeldingId} alt={o.naam ?? ""} />
                  {o.naam && <span className="fawkes-adv-foto-choice__label">{o.naam}</span>}
                </button>
              ))}
            </div>
        {fotoOpties.length === 0 && (
          <p className="fawkes-muted">{t("advertentieDetailView.hoofdfoto.geenOpties")}</p>
        )}
      </div>

      <h4 className="fawkes-adv-detail__blok-title">{t("advertentieDetailView.section.extraFotos")}</h4>
          {mediaPool.length > 0 ? (
            <>
              <div
                className="fawkes-adv-foto-grid"
                role="group"
                aria-label={t("advertentieDetailView.section.extraFotos")}
              >
                {mediaPool.map((m) => (
                  <button
                    key={m.animalFileId}
                    type="button"
                    className={
                      "fawkes-adv-foto-choice" +
                      (fotoSel.has(m.animalFileId) ? " fawkes-adv-foto-choice--selected" : "")
                    }
                    aria-pressed={fotoSel.has(m.animalFileId)}
                    onClick={() => toggleFoto(m.animalFileId)}
                  >
                    {a.dierId != null && (
                      <MediaThumb dierId={String(a.dierId)} fileId={m.animalFileId} alt={m.fileName ?? ""} />
                    )}
                    {m.fileName && <span className="fawkes-adv-foto-choice__label">{m.fileName}</span>}
                  </button>
                ))}
              </div>
              <Button variant="secondary" onClick={onSaveFotos} disabled={busy}>
                {t("advertentieDetailView.extraFotos.opslaan")}
              </Button>
            </>
          ) : (
            <p className="fawkes-muted">{t("advertentieDetailView.extraFotos.geenMedia")}</p>
          )}
        </Modal>
      )}

      {veldPopup && (
        <Modal
          open
          title={
            veldPopup.soort === "tekst"
              ? (BLOKKEN.find((b) => b.veld === veldPopup.veld)?.label ?? "")
              : veldPopup.soort === "tags"
                ? t("advertentieDetailView.field.tags")
                : t("advertentieDetailView.section.afzender")
          }
          onClose={sluitVeld}
          footer={
            <>
              <Button variant="secondary" onClick={sluitVeld} disabled={busy}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={bewaarVeld} disabled={busy}>
                {t("common.save")}
              </Button>
            </>
          }
        >
          <div className="fawkes-adv-detail__form">
            {veldPopup.soort === "tekst" && (
              <div className="fawkes-adv-field">
                {/* Dezelfde twee AI-knoppen als in het formulier: je komt hier juist om de tekst te
                    schrijven, en dan hoort de hulp hier te staan en niet een scherm verderop. */}
                {aiAvailable && (
                  <span className="fawkes-adv-blok-knoppen">
                    <button type="button" className="fawkes-adv-ai__inline"
                      onClick={() => {
                        const blok = BLOKKEN.find((b) => b.veld === veldPopup.veld);
                        if (blok) onBlokOpnieuw(blok.sleutel, blok.veld);
                      }}
                      disabled={busy}>
                      {t("advertentieDetailView.blok.opnieuw")}
                    </button>
                    <button type="button" className="fawkes-adv-ai__inline"
                      onClick={() => {
                        const blok = BLOKKEN.find((b) => b.veld === veldPopup.veld);
                        if (blok) onBlokVerbeter(blok.label, blok.veld);
                      }}
                      disabled={busy || !form[veldPopup.veld]}>
                      {t("advertentieDetailView.blok.verbeter")}
                    </button>
                  </span>
                )}
                <textarea
                  className="fui-field__input fawkes-adv-textarea"
                  autoFocus
                  rows={8}
                  aria-label={BLOKKEN.find((b) => b.veld === veldPopup.veld)?.label}
                  value={form[veldPopup.veld]}
                  onChange={(e) => set(veldPopup.veld, e.target.value)}
                />
              </div>
            )}
            {veldPopup.soort === "tags" && (
              <label className="fawkes-adv-field">
                <span className="fawkes-adv-field__label">{t("advertentieDetailView.field.tags")}</span>
                <input
                  className="fui-field__input"
                  autoFocus
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  placeholder={t("advertentieDetailView.field.tagsPlaceholder")}
                />
                <small className="fawkes-muted">{t("advertentieDetailView.field.tagsHint")}</small>
              </label>
            )}
            {veldPopup.soort === "afzender" && (
              <>
                {([
                  { veld: "afzenderNaam" as const, label: t("advertentieDetailView.field.afzenderNaam") },
                  { veld: "afzenderTelefoon" as const, label: t("advertentieDetailView.field.afzenderTelefoon") },
                  { veld: "afzenderEmail" as const, label: t("advertentieDetailView.field.afzenderEmail") },
                  { veld: "afzenderUrl" as const, label: t("advertentieDetailView.field.afzenderUrl") },
                ]).map((f, i) => (
                  <label key={f.veld} className="fawkes-adv-field">
                    <span className="fawkes-adv-field__label">{f.label}</span>
                    <input
                      className="fui-field__input"
                      autoFocus={i === 0}
                      value={form[f.veld]}
                      onChange={(e) => set(f.veld, e.target.value)}
                    />
                  </label>
                ))}
                <small className="fawkes-muted">{t("advertentieDetailView.field.afzenderHint")}</small>
              </>
            )}
          </div>
        </Modal>
      )}

      {kenmerkenOpen && (
        <Modal
          open
          size="wide"
          title={t("advertentieDetailView.section.kenmerken")}
          onClose={sluitKenmerken}
          footer={
            <>
              <Button variant="secondary" onClick={sluitKenmerken} disabled={busy}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => onSave(() => { setKenmerkenBij(null); setKenmerkenOpen(false); })}
              >
                {t("common.save")}
              </Button>
            </>
          }
        >
          <p className="fawkes-muted">{t("advertentieDetailView.kenmerken.uitleg")}</p>
          {aiAvailable && (
            <div className="fawkes-adv-ai">
              <Button variant="secondary" onClick={vulVragenMetAi} disabled={busy || aiVragenBezig}>
                {t(aiVragenBezig
                  ? "advertentieDetailView.ai.vragenBezig"
                  : "advertentieDetailView.ai.vragen")}
              </Button>
              <span className="fawkes-muted">{t("advertentieDetailView.ai.vragenUitleg")}</span>
            </div>
          )}
        {form.kenmerken.length > 0 ? (
          <ul className="fawkes-adv-detail__kenmerken">
            {form.kenmerken.map((k) => (
              <li key={k.id} className="fawkes-adv-detail__kenmerk">
                <span className="fawkes-adv-detail__kenmerk-label">
                  {t(`advertentieKenmerk.${k.sleutel}`)}
                  {aiBronnen[k.sleutel] && (
                    <small className="fawkes-adv-detail__kenmerk-bron" title={aiBronnen[k.sleutel]}>
                      {t("advertentieDetailView.ai.bron", { args: [aiBronnen[k.sleutel]] })}
                    </small>
                  )}
                </span>
                {/* Het antwoord zoals ikzoekbaas het toont: een keuze uit de lijst van die vraag, en
                        waar de site een toelichting laat zien mag er een zin achter. */}
                    <select
                      className="fui-field__input fui-select fawkes-adv-kenmerk-select"
                      value={antwoordVan(k.waarde)}
                      aria-label={t(`advertentieKenmerk.${k.sleutel}`)}
                      onChange={(e) => {
                        const gekozen = e.target.value;
                        const rest = toelichtingVan(k.waarde);
                        setKenmerk(k.id, {
                          waarde: gekozen === "" ? null : (rest ? `${gekozen}, ${rest}` : gekozen),
                          // Beantwoord is beantwoord — ook een "Nee" hoort straks op de site te staan.
                          status: gekozen === "" ? "ONBEKEND" : "BEVESTIGD",
                        });
                      }}
                    >
                      <option value="">{t("advertentieDetailView.kenmerk.geenAntwoord")}</option>
                      {(vragen.get(k.sleutel)?.antwoorden ?? []).map((a: string) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                    {vragen.get(k.sleutel)?.toelichting && (
                      <input
                        className="fui-field__input fawkes-adv-kenmerk-toelichting"
                        value={toelichtingVan(k.waarde)}
                        placeholder={t("advertentieDetailView.kenmerk.toelichting")}
                        aria-label={t("advertentieDetailView.kenmerk.toelichting")}
                        disabled={antwoordVan(k.waarde) === ""}
                        onChange={(e) => {
                          const kop = antwoordVan(k.waarde);
                          const zin = e.target.value.trim();
                          setKenmerk(k.id, { waarde: zin ? `${kop}, ${zin}` : kop });
                        }}
                      />
                    )}
                    <label className="fawkes-adv-kenmerk-pub">
                      <input
                        type="checkbox"
                        checked={k.publiceerbaar}
                        disabled={k.status !== "BEVESTIGD"}
                        onChange={(e) => setKenmerk(k.id, { publiceerbaar: e.target.checked })}
                      />
                  {t("advertentieDetailView.kenmerk.publiceerbaar")}
                </label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fawkes-muted">{t("advertentieDetailView.empty.kenmerken")}</p>
        )}
        </Modal>
      )}
      {historie && (
        <Modal
          open
          size="wide"
          title={t("advertentieDetailView.historie")}
          onClose={() => setHistorie(false)}
          footer={
            <Button variant="secondary" onClick={() => setHistorie(false)}>
              {t("common.close")}
            </Button>
          }
        >
          <SectionHeader>{t("advertentieDetailView.section.publicatie")}</SectionHeader>
          {!a.publicatieUrl && !a.externeId && !a.publicatiedatum && !a.laatsteSync
            && !a.platformStatus && !a.publicatieKanalen && !a.goedgekeurdDoor && !a.foutmelding
            && !a.platformWachtrij ? (
            <p className="fawkes-muted">{t("advertentieDetailView.publicatie.leeg")}</p>
          ) : (
            <div className="fawkes-adv-detail__grid">
              <Field
                label={t("advertentieDetailView.field.publicatieUrl")}
                value={
                  a.publicatieUrl ? (
                    <a href={a.publicatieUrl} target="_blank" rel="noreferrer noopener">
                      {a.publicatieUrl}
                    </a>
                  ) : null
                }
              />
              <Field label={t("advertentieDetailView.field.externeId")} value={a.externeId} />
              <Field
                label={t("advertentieDetailView.field.publicatiedatum")}
                value={a.publicatiedatum ? formatDateTime(a.publicatiedatum, locale) : null}
              />
              <Field
                label={t("advertentieDetailView.field.laatsteSync")}
                value={a.laatsteSync ? formatDateTime(a.laatsteSync, locale) : null}
              />
              <Field label={t("advertentieDetailView.field.platformStatus")} value={a.platformStatus} />
              <Field label={t("advertentieDetailView.field.publicatieKanalen")} value={a.publicatieKanalen} />
              <Field label={t("advertentieDetailView.field.goedgekeurdDoor")} value={a.goedgekeurdDoor} />
              <Field label={t("advertentieDetailView.field.foutmelding")} value={a.foutmelding} />
              <Field
                label={t("advertentieDetailView.field.platformWachtrij")}
                value={a.platformWachtrij ? wachtrijRegel(a.platformWachtrij, t, locale) : null}
              />
            </div>
          )}

          <SectionHeader>{t("advertentieDetailView.section.log")}</SectionHeader>
          {a.log.length > 0 ? (
            <IndeelbareTabel tabelKey="advertentie-detail" columns={logColumns} rows={a.log} getRowKey={(l) => l.id} empty={t("advertentieDetailView.empty.log")} />
          ) : (
            <p className="fawkes-muted">{t("advertentieDetailView.empty.log")}</p>
          )}
        </Modal>
      )}
    </div>
  );
}
